// [FICHIER] Utilitaire navigation intelligente - Version non-hook pour composants legacy
// [RÔLE] Navigation cross-company sans contraintes React hooks + fallback automatique
// [ARCHITECTURE] Service calls directs + error handling + compatibility layer

import InterviewAccessService from '../services/InterviewAccessService';
import { FamlaServices } from '../services';
import { Routes } from '../routes';
import { navigateToLatestManualMapping, type MappingNavigationState } from './mappingAccess';
import { switchActiveCompany } from './switchActiveCompany';

type SmartNavigate = (
    path: string,
    options?: { replace?: boolean; state?: MappingNavigationState },
) => void;

// [INTERFACE] Options navigation intelligente
interface SmartNavigationOptions {
    onSwitchStart?: () => void;
    onSwitchComplete?: () => void;
    onError?: (error: string) => void;
    replace?: boolean;
}

// [FONCTION PRINCIPALE] Navigation intelligente vers interview (version utilitaire)
export const navigateToInterviewSmart = async (
    processId: string,
    accessToken: string,
    navigate: SmartNavigate,
    options: SmartNavigationOptions = {}
): Promise<boolean> => {
    
    // [VALIDATION] Vérifier paramètres requis
    if (!processId || !accessToken) {
        const error = 'Missing required parameters';
        console.error('❌ [smartNavigation]', error);
        options.onError?.(error);
        return false;
    }

    try {
        console.log('🎯 [smartNavigation] Starting smart navigation to process:', processId);
        
        // [ÉTAPE 1] Vérification accès interview
        const accessResult = await InterviewAccessService.checkInterviewAccess(processId, accessToken);

        if (!accessResult.success) {
            // [ERREUR ACCÈS] Échec vérification accès
            const errorMsg = accessResult.error || 'Failed to check interview access';
            console.error('❌ [smartNavigation] Access check failed:', errorMsg);
            options.onError?.(errorMsg);
            return false;
        }

        if (!accessResult.data?.has_access) {
            // [ACCÈS REFUSÉ] Utilisateur n'a pas accès
            const errorMsg = 'You do not have access to this process';
            console.warn('⚠️ [smartNavigation] Access denied:', errorMsg);
            options.onError?.(errorMsg);
            return false;
        }

        // [ÉTAPE 2] Vérifier si switch nécessaire
        if (accessResult.needsSwitch && accessResult.targetCompanyId) {
            console.log('🔄 [smartNavigation] Company switch needed to:', accessResult.targetCompanyId);
            
            // [SWITCH COMPAGNIE] Notification début switch
            options.onSwitchStart?.();

            const switchSuccess = await switchActiveCompany(
                accessResult.targetCompanyId,
                accessToken,
            );
            
            if (!switchSuccess) {
                const errorMsg = 'Failed to switch company';
                console.error('❌ [smartNavigation] Company switch failed');
                options.onError?.(errorMsg);
                return false;
            }

            options.onSwitchComplete?.();
        }

        // [ÉTAPE 3] Navigation finale vers interview
        console.log('🎯 [smartNavigation] Navigating to interview:', processId);
        navigate(`${Routes.PROCESS_INTERVIEW}${processId}`, { 
            replace: options.replace || false 
        });

        return true;

    } catch (error) {
        // [ERREUR GÉNÉRALE] Gestion erreurs inattendues
        console.error('❌ [smartNavigation] Navigation error:', error);
        const errorMsg = error instanceof Error ? error.message : 'Navigation failed';
        options.onError?.(errorMsg);
        return false;
    }
};

// [FONCTION FALLBACK] Navigation legacy pour compatibilité
export const navigateToInterviewLegacy = async (
    processId: string,
    accessToken: string,
    navigate: (path: string) => void,
    updateSelection?: (id: string) => void
): Promise<boolean> => {
    try {
        console.log('🔄 [smartNavigation] Using legacy navigation for process:', processId);
        
        const data = await FamlaServices("api_host", `/interview/process/${processId}`, "GET", "", accessToken);
        if (data.status === 200) {
            updateSelection?.(data.body.data._id);
            navigate(Routes.PROCESS_INTERVIEW + data.body.data._id);
            return true;
        } else {
            console.error('❌ [smartNavigation] Legacy navigation failed:', data.status);
            return false;
        }
    } catch (error) {
        console.error('❌ [smartNavigation] Legacy navigation error:', error);
        return false;
    }
};

// [FONCTION COMBINÉE] Navigation avec fallback automatique
export const navigateToInterviewWithFallback = async (
    processId: string,
    accessToken: string,
    navigate: SmartNavigate,
    options: SmartNavigationOptions & { updateSelection?: (id: string) => void } = {}
): Promise<boolean> => {
    
    // [TENTATIVE 1] Navigation intelligente
    try {
        const success = await navigateToInterviewSmart(processId, accessToken, navigate, options);
        if (success) {
            return true;
        }
    } catch (error) {
        console.warn('⚠️ [smartNavigation] Smart navigation failed, trying fallback:', error);
    }
    
    // [TENTATIVE 2] Navigation legacy en fallback
    const interviewSuccess = await navigateToInterviewLegacy(
        processId, 
        accessToken, 
        (path: string) => navigate(path), 
        options.updateSelection
    );
    if (interviewSuccess) return true;

    // [TENTATIVE 3] Les processus MANUAL n'ont volontairement aucune interview :
    // ouvrir directement leur dernière feuille de cartographie.
    const manualResult = await navigateToLatestManualMapping({
        processId,
        accessToken,
        navigate: (path, navOptions) =>
            navigate(path, {
                replace: options.replace || false,
                state: navOptions?.state,
            }),
    });
    if (manualResult.success) return true;
    return false;
};
